import React, { useEffect, useRef, useState } from 'react';
import { Header, Container, Button, Segment } from 'semantic-ui-react';
import { Form } from '@plone/components';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { EPICS_PATH_CONTINUE } from 'volto-epics-addon/constants';
import { TASK_UPLOAD_TRIM_PROMPT } from 'volto-base-addon/constants';
import { completeTask, useTask } from 'volto-epics-addon/helpers';
import Body from 'volto-base-addon/components/manage/VideoJS/Body';
import { MEDIA_PAGE_WOWZA_ROUTE } from 'volto-base-addon/constants';
import config from '@plone/volto/registry';
import UploadStepsContainer from './UploadStepsContainer';
import { useIntl, defineMessages } from 'react-intl';
import TimeInput from './TimeInput';
import RangeSlider from './RangeSlider';

const messages = defineMessages({
  uploadDetailsDetails: {
    id: 'uploadDetailsDetails',
    defaultMessage: 'Details',
  },
  uploadDetailsLength: {
    id: 'uploadDetailsLength',
    defaultMessage: 'Video Length',
  },
  uploadDetailsMediaTrimmer: {
    id: 'uploadDetailsMediaTrimmer',
    defaultMessage:
      'A media trimmer allows you to reduce the length of your media',
  },
  uploadDetailsMediaTrimmerResolution: {
    id: 'uploadDetailsMediaTrimmerResolution',
    defaultMessage: '(preview resolution is 360p)',
  },
  mustLoginFirst: {
    id: 'mustLoginFirst',
    defaultMessage: 'You must log in first.',
  },
  uploadDetailsContinue: {
    id: 'uploadDetailsContinue',
    defaultMessage: 'Continue',
  },
});

const Page = () => {
  const intl = useIntl();
  const history = useHistory();
  const task = useTask(TASK_UPLOAD_TRIM_PROMPT);
  const duration = parseFloat(task?.variables?.duration); // in microseconds
  const token = useSelector((state) => state.userSession.token);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [startVal, setStartVal] = useState(0);
  const [endVal, setEndVal] = useState(0);
  const [previewSrc, setPreviewSrc] = useState([{ src: '', type: '' }]);
  const framesetPresignedUrl = task?.variables?.framesetPresignedUrl;
  const operationsPreview = task?.variables?.operationsPreview;
  const s3_object_id = task?.variables?.smilFilename;
  const mediaPageUid = task?.variables?.mediaPageUid;
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const title = task?.variables?.title;
  const destinationTitle = task?.variables?.destinationTitle;

  const spriteBackground = {
    backgroundImage: `url(${framesetPresignedUrl})`,
    backgroundSize: `${dimensions.width}px ${dimensions.height}px`,
    backgroundRepeat: 'no-repeat',
  };

  const isAuthenticated = !!token;
  const isVisible = true;

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });

    const containerRefVar = containerRef.current;
    if (containerRefVar) {
      observer.observe(containerRefVar);
    }

    return () => {
      if (containerRefVar) {
        observer.unobserve(containerRefVar);
      }
      observer.disconnect();
    };
  });

  useEffect(() => {
    let controller = undefined;
    if (task?.completed === true) {
      history.push(EPICS_PATH_CONTINUE);
    } else {
      controller = new AbortController();

      s3_object_id &&
        (async () => {
          const resp = await fetch(
            `${config.settings.publicPath}/${MEDIA_PAGE_WOWZA_ROUTE}/${mediaPageUid}/${s3_object_id}`,
            {
              signal: controller.signal,
            },
          );

          const jsonData = await resp.json();

          if (jsonData['url']) {
            setPreviewSrc([
              {
                src: jsonData['url'],
                type: false,
              },
            ]);
          }
        })();
    }
    return () => {
      controller?.abort();
    };
  }, [task, history, mediaPageUid, s3_object_id]);

  const onSubmit = async (ev) => {
    if (!token) return;

    ev.nativeEvent.preventDefault();

    try {
      completeTask(TASK_UPLOAD_TRIM_PROMPT, {
        trim: JSON.stringify({
          start: startVal,
          end: endVal,
        }),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  useEffect(() => {
    if (duration) {
      setStart(0);
      setEnd(duration / 1e6);
      setStartVal(0);
      setEndVal(duration / 1e6);
    }
  }, [duration]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0');
    const mins = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const secMs = (seconds % 60).toFixed(3).padStart(6, '0');

    return `${hrs}:${mins}:${secMs}`;
  };

  const parseTime = (timeString) => {
    if (!timeString || !timeString.match(/^\d{2}:\d{2}:\d{2}.\d{3}$/)) {
      return 0;
    }

    const [hmsPart, msPart = '000'] = timeString.split('.');
    const [hrs = 0, mins = 0, secs = 0] = hmsPart.split(':').map(Number);
    const ms = parseInt(msPart) || 0;

    // Calculate total seconds with millisecond precision
    return hrs * 3600 + mins * 60 + secs + ms / 1000;
  };

  return isVisible ? (
    isAuthenticated ? (
      <Container className="details-prompt-container">
        <UploadStepsContainer currentStep={3} totalSteps={4} />
        <Header>{intl.formatMessage(messages.uploadDetailsDetails)}</Header>
        {destinationTitle && title ? (
          <p>
            {destinationTitle} / {title}
          </p>
        ) : null}
        <Form onSubmit={onSubmit}>
          <div className="details-prompt-range">
            <div className="range-preview">
              {previewSrc && previewSrc[0].src && (
                <Body
                  data={{
                    options: {
                      audioOnlyMode:
                        operationsPreview?.profiles?.length === 1 &&
                        operationsPreview.profiles[0] === 'Audio',
                      sources: previewSrc,
                      controls: true,
                      responsive: true,
                      playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
                      fluid: true,
                    },
                  }}
                  setPlayerCallback={() => {}}
                  editMode={false}
                />
              )}
              <Segment className="range-segment">
                <p className="trimmer-description">
                  {intl.formatMessage(messages.uploadDetailsMediaTrimmer)}{' '}
                  <strong>
                    {intl.formatMessage(
                      messages.uploadDetailsMediaTrimmerResolution,
                    )}
                  </strong>
                </p>
                <div className="trimmer-time-input-wrapper">
                  <TimeInput
                    label="Start"
                    labelColor="#0066ff"
                    labelAlign="left"
                    value={formatTime(startVal)}
                    onChange={(value) => {
                      const seconds = parseTime(value);
                      const startValResult = Math.min(seconds, endVal - 1);
                      setStartVal(startValResult);
                    }}
                    minTime={formatTime(0)}
                    maxTime={formatTime(endVal - 1)}
                  />
                  <TimeInput
                    label="End"
                    labelColor="#bf4323"
                    labelAlign="right"
                    value={formatTime(endVal)}
                    onChange={(value) => {
                      const seconds = parseTime(value);
                      const endValResult = Math.max(seconds, startVal + 1);
                      setEndVal(endValResult);
                    }}
                    minTime={formatTime(startVal)}
                    maxTime={formatTime(duration / 1e6)}
                  />
                </div>
                <RangeSlider
                  min={start}
                  max={end}
                  startVal={startVal}
                  endVal={endVal}
                  onStartChange={setStartVal}
                  onEndChange={setEndVal}
                  style={spriteBackground}
                  containerRef={containerRef}
                />
                <div className="time-display">
                  <span className="duration">
                    Duration: {formatTime(endVal - startVal)}
                  </span>
                </div>
              </Segment>
            </div>
          </div>
          <Button style={{ marginTop: '1.5em' }} type="submit" floated="right">
            {intl.formatMessage(messages.uploadDetailsContinue)}
          </Button>
        </Form>
      </Container>
    ) : (
      <Container className="details-prompt-container">
        <Header>{intl.formatMessage(messages.mustLoginFirst)}</Header>
      </Container>
    )
  ) : (
    <Container className="details-prompt-container"></Container>
  );
};

export default Page;
